"use client";

import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Archive,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  Database,
  Flag,
  ListTodo,
  NotebookPen,
  PenLine,
  Plus,
  Repeat2,
  Target,
  Trophy,
  UserRound,
  X
} from "lucide-react";
import { useStudio } from "./StudioContext";
import type { StaffGoal, TeamNote, WorkPriority, WorkTask } from "./types";
import { isoDateAtNoon, istanbulToday, monthBounds } from "./lib/date";

const today = istanbulToday;
const currentMonth = () => today().slice(0, 7);
const shortDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "short", year: "numeric" })
        .format(isoDateAtNoon(value.slice(0, 10)))
    : "Tarih yok";
const money = (value: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0
  }).format(value);

const priorityLabel: Record<WorkPriority, string> = {
  LOW: "Düşük",
  NORMAL: "Normal",
  HIGH: "Yüksek",
  URGENT: "Acil"
};

const recurrenceLabel: Record<WorkTask["recurrence"], string> = {
  ONCE: "Tek görev",
  DAILY: "Her gün",
  WEEKLY: "Her hafta",
  MONTHLY: "Her ay"
};

const goalMetricDefinitions: Record<
  StaffGoal["metricCode"],
  {
    label: string;
    title: string;
    metricLabel: string;
    startValue: number;
    targetValue: number;
    aggregation: StaffGoal["aggregation"];
    explanation: string;
  }
> = {
  COMPLETED_SESSIONS: {
    label: "Aylık tamamlanan seans",
    title: "Aylık Tamamlanan Seans Hedefi",
    metricLabel: "Seans",
    startValue: 0,
    targetValue: 500,
    aggregation: "SUM",
    explanation: "Seçilen ayda tamamlanan seanslar programdan otomatik hesaplanır."
  },
  ACTIVE_CUSTOMERS: {
    label: "Aktif müşteri sayısı",
    title: "Aktif Müşteri Sayısı Hedefi",
    metricLabel: "Müşteri",
    startValue: 0,
    targetValue: 100,
    aggregation: "LATEST",
    explanation: "Müşteri kartlarında aktif olan kişilerin güncel toplamı otomatik kullanılır."
  },
  CANCELLATION_RATE: {
    label: "Aylık seans iptal oranı",
    title: "Seans İptal Oranı Hedefi",
    metricLabel: "%",
    startValue: 100,
    targetValue: 25,
    aggregation: "LATEST",
    explanation: "Seçilen aydaki iptal edilen seansların tüm geçerli randevulara oranı otomatik hesaplanır. Düşük oran başarıdır."
  },
  NEW_CUSTOMERS: {
    label: "Aylık yeni müşteri",
    title: "Aylık Yeni Müşteri Hedefi",
    metricLabel: "Müşteri",
    startValue: 0,
    targetValue: 10,
    aggregation: "SUM",
    explanation: "Seçilen ay içinde kayıt tarihi bulunan yeni müşteriler otomatik sayılır."
  },
  GOOGLE_REVIEWS: {
    label: "Aylık yeni Google yorumu",
    title: "Aylık Google Yorumu Hedefi",
    metricLabel: "Yorum",
    startValue: 0,
    targetValue: 5,
    aggregation: "SUM",
    explanation: "Yeni yorum geldikçe hedef kartındaki +1 yorum düğmesi kullanılır. Her ay ayrı hedef olduğu için sayaç sıfırdan başlar."
  },
  CUSTOM: {
    label: "Özel / manuel hedef",
    title: "",
    metricLabel: "Adet",
    startValue: 0,
    targetValue: 1,
    aggregation: "SUM",
    explanation: "Uygulamanın ölçemediği özel sonuçlar ekip tarafından günlük olarak girilir."
  }
};

function displayGoalValue(goal: StaffGoal, value: number) {
  const maximumFractionDigits = goal.metricCode === "CANCELLATION_RATE" ? 1 : 0;
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits }).format(value);
}

function WorkspaceHeading({
  eyebrow,
  title,
  description,
  action
}: {
  eyebrow: string;
  title: string;
  description: string;
  action: React.ReactNode;
}) {
  return (
    <div className="page-heading workspace-heading">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}

function WorkspaceModal({
  title,
  eyebrow,
  onClose,
  children
}: {
  title: string;
  eyebrow: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  return (
    <>
      <button className="modal-scrim" onClick={onClose} aria-label="Kapat" />
      <div className="modal-card workspace-modal" role="dialog" aria-modal="true">
        <div className="drawer-head">
          <div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Kapat"><X size={20} /></button>
        </div>
        {children}
      </div>
    </>
  );
}

function NoteForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { staff, createTeamNote } = useStudio();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      await createTeamNote({
        body: String(data.get("body") || ""),
        priority: String(data.get("priority") || "NORMAL") as WorkPriority,
        dueOn: String(data.get("dueOn") || "") || undefined,
        assignedToId: String(data.get("assignedToId") || "") || undefined,
        authorStaffId: String(data.get("authorStaffId") || "") || undefined
      });
      onSaved();
    } catch {
      setError("Not kaydedilemedi. Bilgileri kontrol edip yeniden deneyin.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <WorkspaceModal title="Yeni ekip notu" eyebrow="Vardiya panosuna bırak" onClose={onClose}>
      <form onSubmit={submit}>
        <label className="field full"><span>Not</span><textarea name="body" rows={5} maxLength={1000} required autoFocus placeholder="Ekip arkadaşlarının bilmesi veya takip etmesi gereken konu…" /></label>
        <div className="form-row">
          <label className="field"><span>Notu bırakan</span><select name="authorStaffId" required defaultValue={staff[0]?.id ?? ""}><option value="" disabled>Kişi seç</option>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label className="field"><span>Kime</span><select name="assignedToId" defaultValue=""><option value="">Tüm ekip</option>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        </div>
        <div className="form-row">
          <label className="field"><span>Öncelik</span><select name="priority" defaultValue="NORMAL"><option value="LOW">Düşük</option><option value="NORMAL">Normal</option><option value="HIGH">Yüksek</option><option value="URGENT">Acil</option></select></label>
          <label className="field"><span>Takip tarihi <i>isteğe bağlı</i></span><input name="dueOn" type="date" min={today()} /></label>
        </div>
        {error && <div className="form-error">{error}</div>}
        <div className="modal-actions"><button type="button" className="button ghost" onClick={onClose}>Vazgeç</button><button className="button primary" disabled={saving}>{saving ? "Kaydediliyor…" : "Notu bırak"}</button></div>
      </form>
    </WorkspaceModal>
  );
}

export function NotesPage({ onToast }: { onToast: (message: string) => void }) {
  const { teamNotes, staff, setTeamNoteStatus } = useStudio();
  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState<"ALL" | "OPEN" | "DONE" | "ARCHIVED">("OPEN");
  const [personFilter, setPersonFilter] = useState("ALL");
  const [changingNoteId, setChangingNoteId] = useState<string | null>(null);
  const visible = teamNotes.filter((note) => {
    const statusMatch = filter === "ALL" || note.status === filter;
    const personMatch =
      personFilter === "ALL" ||
      (personFilter === "TEAM" ? !note.assignedToId : note.assignedToId === personFilter);
    return statusMatch && personMatch;
  });
  const urgent = teamNotes.filter((note) => note.status === "OPEN" && ["HIGH", "URGENT"].includes(note.priority)).length;

  async function changeStatus(note: TeamNote, status: "OPEN" | "DONE" | "ARCHIVED") {
    if (changingNoteId) return;
    if (status === "ARCHIVED" && !window.confirm(`“${note.body}” notunu arşivlemek istiyor musun?`)) return;
    setChangingNoteId(note.id);
    try {
      await setTeamNoteStatus(note, status);
      if (status === "DONE") {
        setFilter("DONE");
        onToast("Not tamamlandı ve Tamamlananlar’a taşındı.");
      } else if (status === "ARCHIVED") {
        setFilter("ARCHIVED");
        onToast("Not arşivlendi; Arşiv görünümünden geri açılabilir.");
      } else {
        setFilter("OPEN");
        onToast("Not yeniden açıldı.");
      }
    } catch {
      onToast("Not güncellenemedi. Lütfen yeniden deneyin.");
    } finally {
      setChangingNoteId(null);
    }
  }

  return (
    <>
      <WorkspaceHeading
        eyebrow="Ekip iletişimi"
        title="Notlar"
        description="Vardiyalar arasında bilgi bırakın, sorumluyu belirleyin ve unutulmadan kapatın."
        action={<button className="button primary" onClick={() => setCreateOpen(true)}><Plus size={18} /> Yeni not</button>}
      />
      <section className="workspace-summary note-summary">
        <div><NotebookPen size={21} /><span><small>Açık not</small><strong>{teamNotes.filter((item) => item.status === "OPEN").length}</strong></span></div>
        <div><Flag size={21} /><span><small>Öncelikli</small><strong>{urgent}</strong></span></div>
        <div><CheckCircle2 size={21} /><span><small>Tamamlanan</small><strong>{teamNotes.filter((item) => item.status === "DONE").length}</strong></span></div>
      </section>
      <div className="workspace-toolbar">
        <div className="segmented">
          {(["OPEN", "DONE", "ARCHIVED", "ALL"] as const).map((item) => (
            <button key={item} className={filter === item ? "active" : ""} onClick={() => setFilter(item)}>
              {item === "OPEN" ? "Açık" : item === "DONE" ? "Tamamlanan" : item === "ARCHIVED" ? "Arşiv" : "Tümü"}
            </button>
          ))}
        </div>
        <div className="workspace-person-filter"><UserRound size={15} /><select value={personFilter} onChange={(event) => setPersonFilter(event.target.value)}><option value="ALL">Tüm kişiler</option><option value="TEAM">Tüm ekibe bırakılan</option>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><span>{visible.length} not</span></div>
      </div>
      <section className="note-board">
        {visible.map((note) => (
          <article className={`team-note priority-${note.priority.toLowerCase()} ${note.status === "DONE" ? "is-done" : ""} ${note.status === "ARCHIVED" ? "is-archived" : ""}`} key={note.id}>
            <button className="note-check" disabled={changingNoteId === note.id} onClick={() => changeStatus(note, note.status === "OPEN" ? "DONE" : "OPEN")} aria-label={note.status === "OPEN" ? "Tamamla" : "Yeniden aç"}>
              {note.status === "OPEN" ? <Circle size={22} /> : note.status === "DONE" ? <CheckCircle2 size={22} /> : <Archive size={20} />}
            </button>
            <div className="note-copy">
              <div className="note-meta"><span className={`work-priority ${note.priority.toLowerCase()}`}>{priorityLabel[note.priority]}</span><span>{note.author} tarafından</span></div>
              <p>{note.body}</p>
              <footer>
                <span><UserRound size={14} /> {note.assignedTo ?? "Tüm ekip"}</span>
                {note.dueOn && <span className={note.status === "OPEN" && note.dueOn < today() ? "overdue" : ""}><CalendarClock size={14} /> {shortDate(note.dueOn)}</span>}
              </footer>
            </div>
            {note.status !== "ARCHIVED" && <button className="icon-button subtle note-archive" disabled={changingNoteId === note.id} onClick={() => changeStatus(note, "ARCHIVED")} aria-label="Arşivle"><Archive size={16} /></button>}
          </article>
        ))}
        {!visible.length && <div className="empty-state workspace-empty"><NotebookPen size={28} /><strong>Bu görünümde not yok</strong><span>Ekibin takip etmesi gereken ilk notu bırakabilirsin.</span><button className="button secondary compact" onClick={() => setCreateOpen(true)}>Not oluştur</button></div>}
      </section>
      {createOpen && <NoteForm onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); onToast("Not ekip panosuna bırakıldı."); }} />}
    </>
  );
}

function TaskForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { staff, createWorkTask } = useStudio();
  const [taskTitle, setTaskTitle] = useState("");
  const [recurrence, setRecurrence] = useState<WorkTask["recurrence"]>("ONCE");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const normalizedTitle = taskTitle.toLocaleLowerCase("tr-TR");
  const recurrenceMismatch =
    (normalizedTitle.includes("haftalık") && recurrence !== "WEEKLY") ||
    (normalizedTitle.includes("günlük") && recurrence !== "DAILY") ||
    (normalizedTitle.includes("aylık") && recurrence !== "MONTHLY");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (recurrenceMismatch) {
      setError("Görev adındaki tekrar sıklığı ile seçilen tekrar alanı uyuşmuyor.");
      return;
    }
    setSaving(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      await createWorkTask({
        title: String(data.get("title") || ""),
        description: String(data.get("description") || "") || undefined,
        assignedToId: String(data.get("assignedToId") || ""),
        priority: String(data.get("priority") || "NORMAL") as WorkPriority,
        recurrence,
        dueOn: String(data.get("dueOn") || ""),
        createdByStaffId: String(data.get("createdByStaffId") || "") || undefined
      });
      onSaved();
    } catch {
      setError("Görev kaydedilemedi. Bilgileri kontrol edip yeniden deneyin.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <WorkspaceModal title="Yeni görev" eyebrow="İşi netleştir ve ata" onClose={onClose}>
      <form onSubmit={submit}>
        <label className="field full"><span>Görev</span><input name="title" required maxLength={180} autoFocus value={taskTitle} onChange={(event) => setTaskTitle(event.target.value)} placeholder="Örn. Haftalık stok kontrolünü tamamla" /></label>
        <label className="field full"><span>Açıklama <i>isteğe bağlı</i></span><textarea name="description" rows={3} maxLength={1000} /></label>
        <div className="form-row">
          <label className="field"><span>Sorumlu</span><select name="assignedToId" required defaultValue={staff[0]?.id ?? ""}><option value="" disabled>Kişi seç</option>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label className="field"><span>Son tarih</span><input name="dueOn" type="date" required defaultValue={today()} /></label>
        </div>
        <div className="form-row">
          <label className="field"><span>Görevi oluşturan</span><select name="createdByStaffId" required defaultValue={staff[0]?.id ?? ""}><option value="" disabled>Kişi seç</option>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label className="field"><span>Tekrar</span><select name="recurrence" value={recurrence} onChange={(event) => setRecurrence(event.target.value as WorkTask["recurrence"])}><option value="ONCE">Tek görev</option><option value="DAILY">Her gün · ertesi gün</option><option value="WEEKLY">Her hafta · 7 gün sonra</option><option value="MONTHLY">Her ay · aynı gün</option></select></label>
        </div>
        {recurrenceMismatch && <div className="form-error">Görev adı ile tekrar seçimi uyuşmuyor. Örneğin “Haftalık” bir görev için “Her hafta” seçilmelidir.</div>}
        <label className="field full"><span>Öncelik</span><select name="priority" defaultValue="NORMAL"><option value="LOW">Düşük</option><option value="NORMAL">Normal</option><option value="HIGH">Yüksek</option><option value="URGENT">Acil</option></select></label>
        {error && <div className="form-error">{error}</div>}
        <div className="modal-actions"><button type="button" className="button ghost" onClick={onClose}>Vazgeç</button><button className="button primary" disabled={saving || recurrenceMismatch}>{saving ? "Kaydediliyor…" : "Görevi ata"}</button></div>
      </form>
    </WorkspaceModal>
  );
}

function GoalForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { staff, createStaffGoal } = useStudio();
  const [metricCode, setMetricCode] = useState<StaffGoal["metricCode"]>("COMPLETED_SESSIONS");
  const [title, setTitle] = useState(goalMetricDefinitions.COMPLETED_SESSIONS.title);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const metric = goalMetricDefinitions[metricCode];
  const manual = metricCode === "CUSTOM" || metricCode === "GOOGLE_REVIEWS";
  const custom = metricCode === "CUSTOM";

  function changeMetric(nextCode: StaffGoal["metricCode"]) {
    setMetricCode(nextCode);
    setTitle(goalMetricDefinitions[nextCode].title);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const data = new FormData(event.currentTarget);
    const month = String(data.get("month") || currentMonth());
    const period = monthBounds(month);
    try {
      await createStaffGoal({
        title,
        metricCode,
        metricLabel: custom ? String(data.get("metricLabel") || "Adet") : metric.metricLabel,
        ownerId: String(data.get("ownerId") || ""),
        periodStart: period.start,
        periodEnd: period.end,
        priority: String(data.get("priority") || "HIGH") as WorkPriority,
        startValue: custom ? Number(data.get("startValue") || 0) : metric.startValue,
        currentValue: custom ? Number(data.get("currentValue") || 0) : metric.startValue,
        targetValue: Number(data.get("targetValue") || 0),
        rewardAmount: Number(data.get("rewardAmount") || 0),
        aggregation: custom ? String(data.get("aggregation") || "SUM") as StaffGoal["aggregation"] : metric.aggregation,
        notes: String(data.get("notes") || "") || undefined
      });
      onSaved();
    } catch {
      setError("Hedef kaydedilemedi. Bilgileri kontrol edip yeniden deneyin.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <WorkspaceModal title="Aylık hedef belirle" eyebrow="Sonucu ve primi birlikte tanımla" onClose={onClose}>
      <form onSubmit={submit}>
        <label className="field full">
          <span>Neyi takip edeceğiz?</span>
          <select value={metricCode} onChange={(event) => changeMetric(event.target.value as StaffGoal["metricCode"])} autoFocus>
            {(Object.entries(goalMetricDefinitions) as Array<[StaffGoal["metricCode"], (typeof goalMetricDefinitions)[StaffGoal["metricCode"]]]>).map(([code, item]) =>
              <option key={code} value={code}>{item.label}</option>
            )}
          </select>
        </label>
        <div className={`goal-source-explainer ${manual ? "manual" : "automatic"}`}>
          {manual ? <PenLine size={18} /> : <Database size={18} />}
          <span><strong>{manual ? "Sonuç ekip tarafından artırılır" : "Sonuç otomatik ölçülür"}</strong>{metric.explanation}</span>
        </div>
        <label className="field full"><span>Hedef adı</span><input name="title" required maxLength={180} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Hedefin kısa adı" /></label>
        <div className="form-row">
          <label className="field"><span>Sorumlu kişi</span><select name="ownerId" required defaultValue={staff[0]?.id ?? ""}><option value="" disabled>Kişi seç</option>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label className="field"><span>Dönem</span><input name="month" type="month" required defaultValue={currentMonth()} /></label>
        </div>
        <div className="form-row">
          {custom
            ? <label className="field"><span>Ölçü birimi</span><input name="metricLabel" required defaultValue={metric.metricLabel} placeholder="Adet, TL, puan…" /></label>
            : <label className="field"><span>Ölçü birimi</span><input value={metric.metricLabel} disabled /></label>}
          <label className="field"><span>Öncelik</span><select name="priority" defaultValue="HIGH"><option value="NORMAL">Normal</option><option value="HIGH">Yüksek</option><option value="URGENT">Kritik</option></select></label>
        </div>
        {custom && <label className="field full"><span>Günlük sonuç hesabı</span><select name="aggregation" defaultValue="SUM"><option value="SUM">Günlük sonuçları topla</option><option value="LATEST">Son girilen değeri kullan</option></select></label>}
        <div className={`goal-number-grid ${custom ? "" : "automatic"}`}>
          {custom && <label className="field"><span>Başlangıç</span><input name="startValue" type="number" step="0.01" defaultValue="0" /></label>}
          {custom && <label className="field"><span>Mevcut</span><input name="currentValue" type="number" step="0.01" defaultValue="0" /></label>}
          <label className="field"><span>Hedef</span><input key={metricCode} name="targetValue" type="number" step="0.01" required defaultValue={metric.targetValue} /></label>
        </div>
        <label className="field full"><span>Hedef primi</span><input name="rewardAmount" type="number" min="0" step="1" defaultValue="0" /></label>
        <label className="field full"><span>Not <i>isteğe bağlı</i></span><textarea name="notes" rows={2} maxLength={1000} /></label>
        {error && <div className="form-error">{error}</div>}
        <div className="modal-actions"><button type="button" className="button ghost" onClick={onClose}>Vazgeç</button><button className="button primary" disabled={saving}>{saving ? "Kaydediliyor…" : "Hedefi oluştur"}</button></div>
      </form>
    </WorkspaceModal>
  );
}

function GoalDailyResultForm({ goal, onClose, onSaved }: { goal: StaffGoal; onClose: () => void; onSaved: () => void }) {
  const { staff, goalDailyEntries, addGoalDailyResult } = useStudio();
  const existingTodayEntry = goalDailyEntries.find(
    (entry) => entry.goalId === goal.id && entry.entryDate === today()
  );
  const [value, setValue] = useState(
    existingTodayEntry?.value ?? (goal.aggregation === "SUM" ? 0 : goal.currentValue)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const entries = goalDailyEntries.filter((entry) => entry.goalId === goal.id).slice(0, 7);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      await addGoalDailyResult({
        goalId: goal.id,
        entryDate: String(data.get("entryDate") || today()),
        value,
        recordedByStaffId: String(data.get("recordedByStaffId") || "") || undefined,
        note: String(data.get("note") || "") || undefined
      });
      onSaved();
    } catch {
      setError("Günlük sonuç kaydedilemedi. Bilgileri kontrol edip yeniden deneyin.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <WorkspaceModal title={goal.metricCode === "GOOGLE_REVIEWS" ? "Yorum sayısını düzelt" : "Günlük hedef sonucu"} eyebrow={`${goal.owner} · ${goal.aggregation === "SUM" ? "günlük değerler toplanır" : "son değer kullanılır"}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="goal-update-summary"><span>{goal.title}</span><strong>{value} / {goal.targetValue} {goal.metricLabel}</strong></div>
        <div className="form-row">
          <label className="field"><span>Gün</span><input name="entryDate" type="date" min={goal.periodStart} max={goal.periodEnd} defaultValue={today()} required /></label>
          <label className="field"><span>{goal.metricCode === "GOOGLE_REVIEWS" ? "O gün gelen yorum" : goal.aggregation === "SUM" ? "O günün sonucu" : "Güncel toplam / oran"}</span><input type="number" min={goal.metricCode === "GOOGLE_REVIEWS" ? 0 : undefined} step={goal.metricCode === "GOOGLE_REVIEWS" ? 1 : 0.01} value={value} onChange={(event) => setValue(Number(event.target.value))} autoFocus required /></label>
        </div>
        <label className="field full"><span>Girişi yapan</span><select name="recordedByStaffId" required defaultValue={staff[0]?.id ?? ""}><option value="" disabled>Kişi seç</option>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label className="field full"><span>Kısa not <i>isteğe bağlı</i></span><input name="note" maxLength={300} placeholder="Örn. Bugün 3 deneme seansı tamamlandı" /></label>
        {!!entries.length && <div className="daily-result-history"><span className="eyebrow">Son girişler</span>{entries.map((entry) => <div key={entry.id}><span>{shortDate(entry.entryDate)} · {entry.recordedBy ?? "THH Portal"}</span><strong>{entry.value} {goal.metricLabel}</strong></div>)}</div>}
        {error && <div className="form-error">{error}</div>}
        <div className="modal-actions"><button type="button" className="button ghost" onClick={onClose}>Vazgeç</button><button className="button primary" disabled={saving}>{saving ? "Kaydediliyor…" : "Günlük sonucu kaydet"}</button></div>
      </form>
    </WorkspaceModal>
  );
}

export function GoalsTasksPage({ onToast }: { onToast: (message: string) => void }) {
  const {
    staff,
    workTasks,
    staffGoals,
    goalDailyEntries,
    setWorkTaskStatus,
    addGoalDailyResult
  } = useStudio();
  const [tab, setTab] = useState<"tasks" | "goals">("tasks");
  const [taskOpen, setTaskOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [progressGoal, setProgressGoal] = useState<StaffGoal | null>(null);
  const [taskFilter, setTaskFilter] = useState<"OPEN" | "DONE" | "ALL">("OPEN");
  const [taskPersonFilter, setTaskPersonFilter] = useState("ALL");
  const [goalOwnerFilter, setGoalOwnerFilter] = useState("ALL");
  const [incrementingGoalId, setIncrementingGoalId] = useState<string | null>(null);
  const [month, setMonth] = useState(currentMonth());
  const visibleTasks = workTasks.filter((task) => {
    const statusMatch = taskFilter === "ALL" ? true : taskFilter === "DONE" ? task.status === "DONE" : task.status !== "DONE";
    return statusMatch && (taskPersonFilter === "ALL" || task.assignedToId === taskPersonFilter);
  });
  const visibleGoals = useMemo(
    () => staffGoals.filter((goal) =>
      goal.periodStart.slice(0, 7) === month &&
      (goalOwnerFilter === "ALL" || goal.ownerId === goalOwnerFilter)
    ),
    [staffGoals, month, goalOwnerFilter]
  );
  const earnedReward = visibleGoals
    .filter((goal) => goal.rewardStatus === "EARNED")
    .reduce((sum, goal) => sum + goal.rewardAmount, 0);

  async function changeTask(task: WorkTask, status: WorkTask["status"] | "CANCELLED") {
    try {
      await setWorkTaskStatus(task, status);
      onToast(status === "DONE" ? "Görev tamamlandı; tekrarlıysa sıradaki görev oluşturuldu." : status === "CANCELLED" ? "Görev iptal edildi." : "Görev güncellendi.");
    } catch {
      onToast("Görev güncellenemedi. Lütfen yeniden deneyin.");
    }
  }

  async function incrementGoogleReview(goal: StaffGoal) {
    const entryDate = today();
    if (entryDate < goal.periodStart || entryDate > goal.periodEnd) {
      onToast("Bu hedef dönemi kapalı. Yorum güncel aya eklenebilir.");
      return;
    }
    const todayEntry = goalDailyEntries.find(
      (entry) => entry.goalId === goal.id && entry.entryDate === entryDate
    );
    setIncrementingGoalId(goal.id);
    try {
      await addGoalDailyResult({
        goalId: goal.id,
        entryDate,
        value: (todayEntry?.value ?? 0) + 1,
        note: "THH Portal üzerinden +1 Google yorumu"
      });
      onToast("Google yorumu hedefe +1 eklendi.");
    } catch {
      onToast("Yorum sayısı artırılamadı. Güncel durumu kontrol edip yeniden deneyin.");
    } finally {
      setIncrementingGoalId(null);
    }
  }

  return (
    <>
      <WorkspaceHeading
        eyebrow="Ekip performansı"
        title="Hedefler & görevler"
        description="Günlük işleri atayın; aylık hedefleri, ilerlemeyi ve kişi bazlı primleri aynı ritimde izleyin."
        action={<div className="heading-actions">{tab === "tasks" ? <button className="button primary" onClick={() => setTaskOpen(true)}><Plus size={18} /> Yeni görev</button> : <button className="button primary" onClick={() => setGoalOpen(true)}><Plus size={18} /> Yeni hedef</button>}</div>}
      />
      <div className="module-tabs workspace-tabs">
        <button className={tab === "tasks" ? "active" : ""} onClick={() => setTab("tasks")}><ListTodo size={17} /> Görevler <i>{workTasks.filter((item) => item.status !== "DONE").length}</i></button>
        <button className={tab === "goals" ? "active" : ""} onClick={() => setTab("goals")}><Target size={17} /> Aylık hedefler <i>{staffGoals.filter((item) => item.status !== "ACHIEVED").length}</i></button>
      </div>

      {tab === "tasks" ? (
        <>
          <section className="workspace-summary task-summary">
            <div><ListTodo size={21} /><span><small>Açık görev</small><strong>{workTasks.filter((item) => item.status !== "DONE").length}</strong></span></div>
            <div><CalendarClock size={21} /><span><small>Bugün</small><strong>{workTasks.filter((item) => item.status !== "DONE" && item.dueOn === today()).length}</strong></span></div>
            <div><Flag size={21} /><span><small>Geciken</small><strong>{workTasks.filter((item) => item.status !== "DONE" && item.dueOn < today()).length}</strong></span></div>
          </section>
          <div className="workspace-toolbar"><div className="segmented">{(["OPEN", "DONE", "ALL"] as const).map((item) => <button key={item} className={taskFilter === item ? "active" : ""} onClick={() => setTaskFilter(item)}>{item === "OPEN" ? "Açık" : item === "DONE" ? "Tamamlanan" : "Tümü"}</button>)}</div><div className="workspace-person-filter"><UserRound size={15} /><select value={taskPersonFilter} onChange={(event) => setTaskPersonFilter(event.target.value)}><option value="ALL">Tüm sorumlular</option>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><span>{visibleTasks.length} görev</span></div></div>
          <section className="task-list">
            {visibleTasks.map((task) => (
              <article className={`work-task ${task.status === "DONE" ? "is-done" : ""}`} key={task.id}>
                <button className="task-check" onClick={() => changeTask(task, task.status === "DONE" ? "TODO" : "DONE")} aria-label={task.status === "DONE" ? "Yeniden aç" : "Tamamla"}>{task.status === "DONE" ? <Check size={17} /> : null}</button>
                <div className="task-main"><div><span className={`work-priority ${task.priority.toLowerCase()}`}>{priorityLabel[task.priority]}</span>{task.recurrence !== "ONCE" && <span className="recurrence-chip"><Repeat2 size={13} /> {recurrenceLabel[task.recurrence]}</span>}</div><h3>{task.title}</h3>{task.description && <p>{task.description}</p>}</div>
                <div className="task-owner"><span className="avatar tiny">{task.assignedTo.split(" ").map((item) => item[0]).slice(0, 2).join("")}</span><span><small>Sorumlu</small><strong>{task.assignedTo}</strong></span></div>
                <div className={`task-due ${task.status !== "DONE" && task.dueOn < today() ? "overdue" : ""}`}><small>Son tarih</small><strong>{shortDate(task.dueOn)}</strong></div>
                <div className="task-actions">{task.status === "TODO" && <button className="text-button" onClick={() => changeTask(task, "IN_PROGRESS")}>Başlat <ChevronRight size={15} /></button>}<button className="icon-button subtle" onClick={() => changeTask(task, "CANCELLED")} aria-label="İptal et"><X size={15} /></button></div>
              </article>
            ))}
            {!visibleTasks.length && <div className="empty-state workspace-empty"><ListTodo size={28} /><strong>Bu görünümde görev yok</strong><span>Günlük, haftalık veya aylık ilk görevi atayabilirsin.</span><button className="button secondary compact" onClick={() => setTaskOpen(true)}>Görev oluştur</button></div>}
          </section>
        </>
      ) : (
        <>
          <section className="goal-period-bar">
            <div className="goal-period-controls"><span className="eyebrow">Hedef dönemi & kişi</span><span><input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /><select value={goalOwnerFilter} onChange={(event) => setGoalOwnerFilter(event.target.value)}><option value="ALL">Tüm kişiler</option>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></span></div>
            <div><span>Toplam hedef</span><strong>{visibleGoals.length}</strong></div>
            <div><span>Tamamlanan</span><strong>{visibleGoals.filter((item) => item.status === "ACHIEVED").length}</strong></div>
            <div className="reward-total"><span>Kazanılan prim</span><strong>{money(earnedReward)}</strong></div>
          </section>
          <section className="goal-grid">
            {visibleGoals.map((goal) => {
              const range = Math.max(0.01, Math.abs(goal.targetValue - goal.startValue));
              const moved = goal.targetValue > goal.startValue
                ? goal.currentValue - goal.startValue
                : goal.startValue - goal.currentValue;
              const progress = Math.max(0, Math.min(100, (moved / range) * 100));
              return (
                <article className={`goal-card ${goal.status === "ACHIEVED" ? "is-achieved" : ""}`} key={goal.id}>
                  <div className="goal-card-head"><span className={`goal-data-source ${goal.dataStatus.toLowerCase()}`}>{goal.trackingMode === "AUTO_SQL" ? <Database size={13} /> : <PenLine size={13} />}{goal.dataStatus === "LIVE" ? "Otomatik güncel" : "Manuel takip"}</span><span className={`goal-status ${goal.status.toLowerCase()}`}>{goal.status === "ACHIEVED" ? "Tamamlandı" : goal.status === "IN_PROGRESS" ? "Devam ediyor" : "Başlamadı"}</span></div>
                  <h3>{goal.title}</h3>
                  <div className="goal-owner"><span className="avatar tiny">{goal.owner.split(" ").map((item) => item[0]).slice(0, 2).join("")}</span><span><small>Hedef sahibi</small><strong>{goal.owner}</strong></span></div>
                  <div className="goal-rhythm"><span style={{ width: `${progress}%` }} /><i style={{ left: `${progress}%` }} /></div>
                  <div className="goal-values"><span><small>Canlı sonuç</small><strong>{displayGoalValue(goal, goal.currentValue)} <i>{goal.metricLabel}</i></strong></span><span><small>Hedef</small><strong>{displayGoalValue(goal, goal.targetValue)} {goal.metricLabel}</strong></span></div>
                  {goal.dataMessage && <p className={`goal-data-message ${goal.dataStatus.toLowerCase()}`}>{goal.dataMessage}</p>}
                  <footer>
                    <span><Trophy size={16} /> {money(goal.rewardAmount)} prim</span>
                    {goal.metricCode === "GOOGLE_REVIEWS" ? (
                      <span className="goal-manual-actions">
                        <button className="text-button" onClick={() => setProgressGoal(goal)}>Düzelt</button>
                        <button className="button secondary compact" disabled={incrementingGoalId === goal.id || today() < goal.periodStart || today() > goal.periodEnd} onClick={() => void incrementGoogleReview(goal)}>
                          <Plus size={15} /> {incrementingGoalId === goal.id ? "Ekleniyor…" : "+1 yorum"}
                        </button>
                      </span>
                    ) : goal.trackingMode === "MANUAL" ? (
                      <button className="text-button" onClick={() => setProgressGoal(goal)}>Günlük sonuç gir <ChevronRight size={15} /></button>
                    ) : (
                      <span className="goal-auto-caption">Kendiliğinden güncellenir</span>
                    )}
                  </footer>
                </article>
              );
            })}
            {!visibleGoals.length && <div className="empty-state workspace-empty goal-empty"><Target size={29} /><strong>Bu ay için hedef yok</strong><span>Kişi bazlı hedefi ve başarı primini birlikte belirleyebilirsin.</span><button className="button secondary compact" onClick={() => setGoalOpen(true)}>Hedef oluştur</button></div>}
          </section>
        </>
      )}
      {taskOpen && <TaskForm onClose={() => setTaskOpen(false)} onSaved={() => { setTaskOpen(false); onToast("Görev atandı."); }} />}
      {goalOpen && <GoalForm onClose={() => setGoalOpen(false)} onSaved={() => { setGoalOpen(false); onToast("Aylık hedef oluşturuldu."); }} />}
      {progressGoal && <GoalDailyResultForm goal={progressGoal} onClose={() => setProgressGoal(null)} onSaved={() => { setProgressGoal(null); onToast("Günlük hedef sonucu kaydedildi."); }} />}
    </>
  );
}
